/**
 * Constants and configuration defaults for BlogPostSelectorComponent
 * 
 * @remarks
 * This file contains all configurable constants used by the blog post selector,
 * including thresholds, defaults, date presets, and UI configuration values.
 * 
 * These values can be overridden via the BlogPostSelectorConfig interface.
 */

/**
 * Default initial load threshold
 * If the number of posts in an organization is below this threshold,
 * they will be displayed immediately without requiring a search.
 * 
 * @default 50
 */
export const DEFAULT_INITIAL_LOAD_THRESHOLD = 50;

/**
 * Default minimum search term length before triggering a search
 * 
 * @default 3
 */
export const DEFAULT_MIN_SEARCH_LENGTH = 3;

/**
 * Default debounce time in milliseconds for search input
 * 
 * @default 300
 */
export const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

/**
 * Default debounce time in milliseconds for filter changes
 * (applies to dropdowns and other filters)
 * 
 * @default 150
 */
export const DEFAULT_FILTER_DEBOUNCE_MS = 150;

/**
 * Maximum number of posts to fetch in a single request
 * Aligned with API rate limits
 * 
 * @default 50
 */
export const DEFAULT_MAX_RESULTS_PER_PAGE = 50;

/**
 * Maximum number of authors to load for the author filter
 * 
 * @default 500
 */
export const DEFAULT_MAX_AUTHORS = 500;

/**
 * Date format for ISO 8601 (used for API communication)
 * All dates are sent to API in UTC timezone
 * 
 * @default 'yyyy-MM-dd'
 */
export const ISO_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Date presets for quick date range selection
 * Each preset includes a label and a function to calculate the date range
 */
export interface DatePreset {
  /** Display label for the preset */
  label: string;
  
  /** Unique identifier for the preset */
  value: string;
  
  /** 
   * Function to calculate the date range
   * Returns [startDate, endDate] in ISO 8601 format (UTC)
   */
  getDateRange: () => { dateFrom: string; dateTo: string };
}

/**
 * Predefined date range presets
 * All dates are calculated relative to current UTC time
 */
export const DATE_PRESETS: DatePreset[] = [
  {
    label: 'Last 7 Days',
    value: 'last_7_days',
    getDateRange: () => {
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      return {
        dateFrom: sevenDaysAgo.toISOString().split('T')[0],
        dateTo: today.toISOString().split('T')[0]
      };
    }
  },
  {
    label: 'Last 30 Days',
    value: 'last_30_days',
    getDateRange: () => {
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      return {
        dateFrom: thirtyDaysAgo.toISOString().split('T')[0],
        dateTo: today.toISOString().split('T')[0]
      };
    }
  },
  {
    label: 'Last 90 Days',
    value: 'last_90_days',
    getDateRange: () => {
      const today = new Date();
      const ninetyDaysAgo = new Date(today);
      ninetyDaysAgo.setDate(today.getDate() - 90);
      return {
        dateFrom: ninetyDaysAgo.toISOString().split('T')[0],
        dateTo: today.toISOString().split('T')[0]
      };
    }
  },
  {
    label: 'Last 6 Months',
    value: 'last_6_months',
    getDateRange: () => {
      const today = new Date();
      const sixMonthsAgo = new Date(today);
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      return {
        dateFrom: sixMonthsAgo.toISOString().split('T')[0],
        dateTo: today.toISOString().split('T')[0]
      };
    }
  },
  {
    label: 'Last Year',
    value: 'last_year',
    getDateRange: () => {
      const today = new Date();
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(today.getFullYear() - 1);
      return {
        dateFrom: oneYearAgo.toISOString().split('T')[0],
        dateTo: today.toISOString().split('T')[0]
      };
    }
  },
  {
    label: 'Custom Range',
    value: 'custom',
    getDateRange: () => ({
      dateFrom: '',
      dateTo: ''
    })
  }
];

/**
 * UI animation durations in milliseconds
 */
export const ANIMATION_DURATION = {
  /** Accordion expand/collapse animation (Phase 3) */
  ACCORDION: 300,
  
  /** Filter chip fade in/out */
  FILTER_CHIP: 200,
  
  /** Button press feedback */
  BUTTON_PRESS: 150,
  
  /** Loading skeleton pulse */
  SKELETON_PULSE: 1500,
  
  /** Chevron rotation duration (Phase 3) */
  CHEVRON_ROTATION: 300,
  
  /** Filter badge scale animation (Phase 3) */
  BADGE_SCALE: 200
} as const;

/**
 * Component text labels and messages
 */
export const SELECTOR_LABELS = {
  /** Default dialog title */
  DEFAULT_TITLE: 'Select Blog Posts',
  
  /** Default primary button text */
  DEFAULT_PRIMARY_BUTTON: 'Select',
  
  /** Default secondary button text */
  DEFAULT_SECONDARY_BUTTON: 'Cancel',
  
  /** Advanced filters toggle button text (Phase 3) */
  ADVANCED_FILTERS_TOGGLE: 'Advanced Filters',
  
  /** Clear all filters button text (Phase 3) */
  CLEAR_ALL_FILTERS: 'Clear all filters',
  
  /** Active filters count label (Phase 3) */
  ACTIVE_FILTERS_COUNT: '{count} active filter{plural}',
  
  /** Messages for different states */
  MESSAGES: {
    INITIAL_STATE: 'Type at least {minLength} characters to search for blog posts',
    NO_RESULTS: 'No posts found',
    NO_RESULTS_HINT: 'Try adjusting your search term or filters',
    LOADING: 'Searching posts...',
    ERROR: 'Failed to search posts. Please try again.',
    THRESHOLD_EXCEEDED: 'Too many posts to display. Please use search or filters to narrow down results.',
    SHOWING_ALL: 'Showing all {count} posts',
    SHOWING_FILTERED: 'Showing {filtered} of {total} posts'
  }
} as const;

/**
 * Default filter states
 */
export const DEFAULT_FILTER_STATE = {
  /** Show advanced filters by default (Phase 3: Initially collapsed) */
  SHOW_ADVANCED_FILTERS: false,
  
  /** Enable date filters by default (Phase 4) */
  ENABLE_DATE_FILTER: true,
  
  /** Enable author filter by default (Phase 5) */
  ENABLE_AUTHOR_FILTER: true,
  
  /** Enable post type filter by default (Phase 6) */
  ENABLE_POST_TYPE_FILTER: true,
  
  /** Enable status filter by default */
  ENABLE_STATUS_FILTER: true,
  
  /** Enable search functionality by default */
  ENABLE_SEARCH: true,
  
  /** Enable initial load by default (Phase 2) */
  ENABLE_INITIAL_LOAD: true,
  
  /** Show threshold message by default (Phase 2) */
  SHOW_THRESHOLD_MESSAGE: true
} as const;

/**
 * Accessibility labels for screen readers (Phase 3)
 */
export const ARIA_LABELS = {
  /** Advanced filters toggle button */
  TOGGLE_ADVANCED_FILTERS: 'Toggle advanced filters',
  
  /** Clear all filters button */
  CLEAR_ALL_FILTERS: 'Clear all active filters',
  
  /** Active filters count */
  ACTIVE_FILTERS_COUNT: '{count} active filter{plural}',
  
  /** Advanced filters panel */
  ADVANCED_FILTERS_PANEL: 'Advanced filters panel',
  
  /** Status filter button */
  FILTER_BY_STATUS: 'Filter by {status} status ({count} posts)',
  
  /** Select all button */
  SELECT_ALL: 'Select all filtered posts',
  
  /** Deselect all button */
  DESELECT_ALL: 'Deselect all posts',
  
  /** Search input */
  SEARCH_POSTS: 'Search posts by title',
  
  /** Clear search button */
  CLEAR_SEARCH: 'Clear search',
  
  /** Clear author filter button (Phase 5) */
  CLEAR_AUTHOR_FILTER: 'Clear author filter',
  
  /** Clear post type filter button (Phase 6) */
  CLEAR_POST_TYPE_FILTER: 'Clear post type filter'
} as const;

/**
 * Cache configuration
 */
export const CACHE_CONFIG = {
  /** Cache expiration time in milliseconds (5 minutes) */
  EXPIRATION_MS: 5 * 60 * 1000,
  
  /** Maximum number of cached entries */
  MAX_ENTRIES: 50
} as const;

/**
 * API rate limit information (from BLOG_POST_API_REFERENCE.md)
 * Used for optimizing request patterns
 */
export const API_RATE_LIMITS = {
  /** Public endpoints: requests per 15 minutes */
  PUBLIC_LIST: 100,
  PUBLIC_DETAIL: 200,
  
  /** Protected endpoints have standard rate limits */
  PROTECTED: 'standard'
} as const;

/**
 * Date utility functions
 */

/**
 * Converts a date to ISO 8601 format (YYYY-MM-DD) in UTC
 * 
 * @param date - Date to convert
 * @returns ISO 8601 date string or empty string if invalid
 */
export function toISODateString(date: Date | string | null | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';
    
    return dateObj.toISOString().split('T')[0];
  } catch {
    return '';
  }
}

/**
 * Gets today's date in ISO 8601 format (YYYY-MM-DD) in UTC
 * 
 * @returns Today's date as ISO 8601 string
 */
export function getTodayISOString(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Validates that a date range is valid (from <= to)
 * 
 * @param dateFrom - Start date
 * @param dateTo - End date
 * @returns True if the range is valid or if either date is empty
 */
export function isValidDateRange(dateFrom: string, dateTo: string): boolean {
  if (!dateFrom || !dateTo) return true;
  
  try {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    return from <= to;
  } catch {
    return false;
  }
}

/**
 * Formats a date string for display in the user's locale
 * 
 * @param dateString - ISO 8601 date string
 * @param locale - Optional locale (defaults to user's locale)
 * @returns Formatted date string or empty string if invalid
 */
export function formatDateForDisplay(dateString: string, locale?: string): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    return new Intl.DateTimeFormat(locale || navigator.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  } catch {
    return dateString;
  }
}
